// ONB-PARENT step 4: sync the calendar (mock rev3 step 3). Built, registered, and OFF: GOOGLE_STEP.enabled stays false until the Google app is verified.
//   The sign-in happens in a POPUP (the wizard never navigates): a blank window is opened synchronously on the Sync click, then pointed at the consent URL the server mints.
//   The result comes back three ways and is never trusted from one alone: a BroadcastChannel message from the callback page, a poll of profiles.google_calendar_enabled / _broken,
//   and popup.closed. A message only triggers a re-read of the profile; "connected" always means the profile says so.
//   isDone  = connected (enabled, not broken). commit writes nothing (the connection is made by Sync). Skip and Next differ only by the gate.
//   Closing the wizard (destroy) stops the poll and the channel and ignores a late result.
const H = () => window.HUD;
const T = (k, v) => H().T(k, v);
const el = (tag, cls, txt) => { const e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; };

// the flag: ONB-4 ships dark. A test (or QA) flips it through HUD_ONB._test; production never does.
export const GOOGLE_STEP = { enabled: false, pollMs: 1500, closeGraceMs: 1200 };   // pollMs / closeGraceMs: a test shortens them

// Google refuses OAuth inside embedded webviews (disallowed_useragent): detect the common ones and send the user to their browser
export function isEmbeddedWebview(ua) {
  const s = String(ua || '');
  return /FBAN|FBAV|FB_IAB|Instagram|MicroMessenger|Snapchat|TikTok|Line\/|; wv\)|\bGSA\//i.test(s);
}
export function isStandaloneApp() {
  return window.navigator.standalone === true || !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}
export function connectedNow(s) { return !!(s && s.googleCalendarEnabled && !s.googleCalendarBroken); }
export function applicable() { return GOOGLE_STEP.enabled === true; }

export const googleStep = {
  id: 'calendar', order: 40,
  applicable,
  label: () => T('onb_gcal_label'),
  summary: (r) => (r === 'done' ? T('onb_sum_gcal_done') : T('onb_sum_skipped', { label: T('onb_gcal_label') })),
  mount(api) {
    const h = H();
    const root = el('div');
    const ic = el('div', 'onb-icon', '📆'); ic.setAttribute('aria-hidden', 'true');
    const title = el('h2'), sub = el('p', 'onb-sub');
    const card = el('div', 'onb-switchrow onb-gcard'); const cic = el('span', 'onb-sr-ic', '🟦'); cic.setAttribute('aria-hidden', 'true');
    const tx = el('span', 'onb-sr-tx'); const ct = el('b'), cs = el('small'); tx.append(ct, cs);
    const act = el('span', 'onb-gact'); const btn = el('button', 'onb-sync-btn'); btn.type = 'button';
    const spin = el('span', 'onb-spin'); spin.setAttribute('aria-hidden', 'true'); const ok = el('span', 'onb-sync-ok', '✓'); ok.setAttribute('aria-hidden', 'true');
    act.append(btn, spin, ok); card.append(cic, tx, act);
    const note = el('p', 'onb-note'); note.setAttribute('data-gcal-note', '1');
    const err = el('p', 'onb-error'); err.setAttribute('role', 'alert');
    root.append(ic, title, sub, card, note, err);

    const ua = navigator.userAgent;
    const webview = isEmbeddedWebview(ua);
    // mode: idle | broken | connecting | connected | blocked | cancelled | error | webview
    let mode = webview ? 'webview' : (connectedNow(h.session) ? 'connected' : (h.session && h.session.googleCalendarEnabled && h.session.googleCalendarBroken ? 'broken' : 'idle'));
    let attempt = 0, pop = null, pollT = null, closedT = null, chan = null, dead = false, checking = false;

    function paint() {
      const connecting = mode === 'connecting', connected = mode === 'connected';
      ct.textContent = T('onb_gcal_card_title');
      cs.textContent = connecting ? T('onb_gcal_connecting') : connected ? T('onb_gcal_connected') : mode === 'broken' ? T('onb_gcal_broken') : T('onb_gcal_card_sub');
      btn.textContent = mode === 'broken' || mode === 'cancelled' || mode === 'error' || mode === 'blocked' ? T('onb_gcal_reconnect') : T('onb_gcal_btn');
      btn.hidden = connecting || connected; btn.disabled = mode === 'webview';
      spin.hidden = !connecting; ok.hidden = !connected;
      card.classList.toggle('is-on', connected);
      note.textContent = mode === 'webview' ? T('onb_gcal_webview') : T('onb_gcal_note') + (isStandaloneApp() && !connected ? ' ' + T('onb_gcal_ios_note') : '');
      err.textContent = mode === 'blocked' ? T('onb_gcal_blocked') : mode === 'cancelled' ? T('onb_gcal_cancelled') : mode === 'error' ? T('onb_gcal_error') : '';
      err.hidden = !err.textContent;
    }
    function texts() { title.textContent = T('onb_gcal_title'); sub.textContent = T('onb_gcal_sub'); paint(); }

    function stopWatch() {
      if (pollT) { clearInterval(pollT); pollT = null; }
      if (closedT) { clearTimeout(closedT); closedT = null; }
      if (chan) { try { chan.close(); } catch (_) {} chan = null; }
    }
    function closePopup() { try { if (pop && !pop.closed) pop.close(); } catch (_) {} pop = null; }
    // the truth is the profile row
    async function readProfile() {
      const s = h.session; if (!s || !s.id) return null;
      const { data, error } = await h._supa.from('profiles').select('google_calendar_enabled,google_calendar_broken').eq('id', s.id).limit(1);
      if (error) { console.error('❌ onboarding google status:', error); return null; }
      return (data && data[0]) || null;
    }
    async function check(gen) {
      if (checking) return false; checking = true;
      try {
        const p = await readProfile();
        if (dead || gen !== attempt) return false;
        if (p && p.google_calendar_enabled && !p.google_calendar_broken) {
          h.session.googleCalendarEnabled = true; h.session.googleCalendarBroken = false;
          stopWatch(); closePopup(); mode = 'connected'; paint(); api.refresh(); return true;
        }
        return false;
      } finally { checking = false; }
    }
    function fail(gen, m) { if (dead || gen !== attempt) return; stopWatch(); closePopup(); mode = m; paint(); api.refresh(); }
    function watch(gen) {
      try {
        if (typeof BroadcastChannel === 'function') {
          chan = new BroadcastChannel('hud-google');
          chan.onmessage = (ev) => {
            const d = ev && ev.data; if (!d || d.type !== 'hud-google' || dead || gen !== attempt) return;
            if (d.ok === false) { fail(gen, 'error'); return; }
            check(gen).then((done) => { if (!done) { /* the poll keeps going */ } });
          };
        }
      } catch (_) { chan = null; }
      pollT = setInterval(async () => {
        if (dead || gen !== attempt) { stopWatch(); return; }
        if (await check(gen)) return;
        if (pop && pop.closed && !closedT) {
          // the window is gone: one last read after a short grace (the callback may have closed it right after the write), then it is a cancel
          closedT = setTimeout(async () => { closedT = null; if (dead || gen !== attempt) return; if (!(await check(gen))) fail(gen, 'cancelled'); }, GOOGLE_STEP.closeGraceMs);
        }
      }, GOOGLE_STEP.pollMs);
    }
    btn.addEventListener('click', () => {
      if (mode === 'connecting' || mode === 'connected' || mode === 'webview') return;
      // the popup must open synchronously inside this click or a blocker eats it
      let w = null; try { w = window.open('about:blank', 'hud_google', 'width=500,height=680'); } catch (_) { w = null; }
      if (!w) { mode = 'blocked'; paint(); return; }
      pop = w; mode = 'connecting'; const gen = ++attempt; paint(); api.refresh();
      (async () => {
        const r = await h._googleAuthStartPopup();
        if (dead || gen !== attempt) { try { w.close(); } catch (_) {} return; }
        if (!r || !r.url) { fail(gen, 'error'); return; }
        try { w.location.href = r.url; } catch (_) { fail(gen, 'error'); return; }
        watch(gen);
      })().catch((e) => { console.error('❌ onboarding google start:', e); fail(gen, 'error'); });
    });
    texts();
    return {
      el: root,
      isDone: () => mode === 'connected',
      async commit() { return { ok: true }; },
      relang: texts,
      // coming (back) to the step re-reads the profile: a sign-in that finished while the user was elsewhere shows as connected
      onEnter() { if (mode === 'connecting' || mode === 'webview' || mode === 'connected') return; const gen = attempt; readProfile().then((p) => { if (dead || gen !== attempt || mode === 'connecting' || mode === 'connected') return; if (p && p.google_calendar_enabled && !p.google_calendar_broken) { h.session.googleCalendarEnabled = true; h.session.googleCalendarBroken = false; mode = 'connected'; paint(); api.refresh(); } }); },
      // leaving the step (Back, Skip, Next) abandons a sign-in that is still open: the poll stops, the window closes, a late result is ignored
      onLeave() { if (mode === 'connecting') { attempt++; stopWatch(); closePopup(); mode = 'idle'; paint(); } },
      destroy() { dead = true; attempt++; stopWatch(); closePopup(); },
    };
  },
};
